const DATE_FORMAT = "Y-m-d H:i";
const PICKER_SELECTOR = "[data-episode-schedule-picker]";
const INVALID_TIME_ZONE_MESSAGE = "The show time zone is unavailable.";
const MINUTE_IN_MILLISECONDS = 60 * 1000;

/**
 * Formats an instant as a complete wall-clock minute in the supplied IANA zone.
 *
 * @param {Date} instant absolute time to format.
 * @param {string} timeZone IANA show time zone.
 * @returns {string|null} show-local YYYY-MM-DD HH:MM value, or null when unsupported.
 */
function formatShowMinute(instant, timeZone) {
  if (
    typeof Intl === "undefined" ||
    typeof Intl.DateTimeFormat !== "function" ||
    typeof timeZone !== "string" ||
    timeZone.length === 0
  ) {
    return null;
  }

  try {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    });
    const values = formatter.formatToParts(instant).reduce((result, part) => {
      if (part.type !== "literal") result[part.type] = part.value;
      return result;
    }, {});
    const requiredParts = ["year", "month", "day", "hour", "minute"];
    if (requiredParts.some((part) => !values[part])) return null;

    return `${values.year}-${values.month}-${values.day} ${values.hour}:${values.minute}`;
  } catch (_error) {
    return null;
  }
}

/**
 * Calculates the Flatpickr boundary for one show-local scheduling purpose.
 *
 * @param {Object} options boundary inputs.
 * @param {Date} options.now current absolute time.
 * @param {string} options.timeZone IANA show time zone.
 * @param {string} options.purpose schedule or publication.
 * @returns {{minDate: string}|{maxDate: string}|null} Flatpickr boundary.
 */
export function episodeScheduleBoundary({ now, timeZone, purpose }) {
  if (!(now instanceof Date) || Number.isNaN(now.getTime())) return null;

  let boundaryTime;
  let boundaryName;
  if (purpose === "schedule") {
    boundaryTime = new Date(
      Math.ceil((now.getTime() + MINUTE_IN_MILLISECONDS) / MINUTE_IN_MILLISECONDS) *
        MINUTE_IN_MILLISECONDS,
    );
    boundaryName = "minDate";
  } else if (purpose === "publication") {
    boundaryTime = new Date(
      Math.floor(now.getTime() / MINUTE_IN_MILLISECONDS) * MINUTE_IN_MILLISECONDS,
    );
    boundaryName = "maxDate";
  } else {
    return null;
  }

  const wallClock = formatShowMinute(boundaryTime, timeZone);
  return wallClock ? { [boundaryName]: wallClock } : null;
}

function setTimeZoneValidity(input, valid) {
  if (typeof input?.setCustomValidity !== "function") return;

  input.setCustomValidity(valid ? "" : INVALID_TIME_ZONE_MESSAGE);
}

/**
 * Selects the earliest valid scheduling minute when Flatpickr has no selection.
 *
 * @param {HTMLInputElement} input picker input with scheduling-purpose metadata.
 * @param {Object} instance Flatpickr instance.
 * @param {Object|null} boundary current show-local boundary.
 * @returns {boolean} whether the scheduling minimum was selected.
 */
function selectSchedulingMinimum(input, instance, boundary) {
  const minimum = boundary?.minDate;
  if (
    input?.dataset?.episodeSchedulePurpose !== "schedule" ||
    !minimum ||
    !Array.isArray(instance?.selectedDates) ||
    instance.selectedDates.length > 0 ||
    typeof instance.setDate !== "function"
  ) {
    return false;
  }

  instance.setDate(minimum, false, DATE_FORMAT);
  return true;
}

/**
 * Recalculates one live Flatpickr boundary without changing its selected value.
 *
 * @param {HTMLInputElement} input initialized picker input.
 * @param {Object} instance Flatpickr instance.
 * @param {Function} now current-time provider.
 * @returns {Object|null} refreshed boundary, or null when formatting fails closed.
 */
function refreshBoundary(input, instance, now) {
  const boundary = episodeScheduleBoundary({
    now: now(),
    timeZone: input.dataset.episodeScheduleTimeZone,
    purpose: input.dataset.episodeSchedulePurpose,
  });
  setTimeZoneValidity(input, Boolean(boundary));
  if (!boundary) return null;

  Object.entries(boundary).forEach(([option, value]) => instance.set(option, value));
  selectSchedulingMinimum(input, instance, boundary);
  return boundary;
}

/**
 * Initializes one show-zone-aware episode scheduling picker.
 *
 * @param {HTMLInputElement} input picker input with show-zone data attributes.
 * @param {Object} options runtime dependencies and Flatpickr options.
 * @param {Function} options.flatpickr Flatpickr initializer.
 * @param {Function} [options.now] current-time provider.
 * @param {Object} [options.pickerOptions] existing surface-specific options.
 * @returns {Object|null} Flatpickr instance, or null when initialization fails closed.
 */
export function initializeEpisodeSchedulePicker(
  input,
  { flatpickr, now = () => new Date(), pickerOptions = {} } = {},
) {
  if (!input || typeof flatpickr !== "function") return null;

  const boundary = episodeScheduleBoundary({
    now: now(),
    timeZone: input.dataset?.episodeScheduleTimeZone,
    purpose: input.dataset?.episodeSchedulePurpose,
  });
  setTimeZoneValidity(input, Boolean(boundary));
  if (!boundary) return null;

  const instance = flatpickr(input, {
    ...pickerOptions,
    enableTime: true,
    dateFormat: DATE_FORMAT,
    ...boundary,
    onOpen(_selectedDates, _dateString, instance) {
      refreshBoundary(input, instance, now);
    },
  });
  selectSchedulingMinimum(input, instance, boundary);
  return instance;
}

/**
 * Initializes every uninitialized episode scheduling picker in a page or subtree.
 *
 * @param {Document|HTMLElement} root page or subtree to initialize.
 * @param {Object} options dependencies passed to each picker.
 * @returns {Object[]} initialized Flatpickr instances.
 */
export function initializeEpisodeSchedulePickers(root = document, options = {}) {
  if (!root || typeof root.querySelectorAll !== "function") return [];

  return Array.from(root.querySelectorAll(PICKER_SELECTOR)).reduce((instances, input) => {
    if (input._flatpickr) return instances;

    const instance = initializeEpisodeSchedulePicker(input, options);
    if (instance) instances.push(instance);
    return instances;
  }, []);
}
